import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCommunity } from "@/pages/CommunityLayout";
import { useCommunityBrand } from "@/hooks/useCommunityBrand";
import { ResidentDoor } from "@/components/ResidentDoor";
import { ShieldCheck, MessageSquareText, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Login() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();

  // Community-branded sign-in when arriving from a community's front door (/c/:slug/login
  // or /login?community=slug). Cosmetic only — access is still decided by membership.
  const { slug: ctxSlug, site: ctxSite } = useCommunity();
  const [params] = useSearchParams();
  const routeParams = useParams();
  const brandSlug = ctxSlug || routeParams.slug || params.get("community") || "";
  const fetched = useCommunityBrand(ctxSite ? "" : brandSlug);
  const brand = ctxSite
    ? { community: ctxSite.community, brand_color: ctxSite.brand_color, has_logo: ctxSite.has_logo }
    : fetched;
  const accent = (brand && brand.brand_color) || "";
  const logoUrl = brand && brand.has_logo && brandSlug ? `${API}/public/site/${brandSlug}/logo` : "";

  const postLoginDest = () => {
    try { const s = localStorage.getItem("rl_post_login"); if (s && s.startsWith("/dashboard")) { localStorage.removeItem("rl_post_login"); return s; } } catch { /* noop */ }
    return "/dashboard";
  };

  useEffect(() => {
    if (!loading && user) navigate(postLoginDest(), { replace: true });
  }, [user, loading, navigate]);

  // The Google button is an iframe with a fixed pixel width — size it to the column so a phone
  // never gets a page wider than its screen.
  const doorRef = useRef(null);
  const [googleWidth, setGoogleWidth] = useState(384);
  useEffect(() => {
    const measure = () => { if (doorRef.current) setGoogleWidth(Math.max(200, Math.min(400, doorRef.current.clientWidth))); };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const res = await api.post("/auth/google", { credential: credentialResponse.credential });
      setUser(res.data);
      navigate(postLoginDest(), { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sign in failed. Please try again.");
    }
  };

  const [ident, setIdent] = useState("");
  const [password, setPassword] = useState("");
  const [step, setStep] = useState("identify");
  const [busy, setBusy] = useState(false);
  const [lookupRes, setLookupRes] = useState(null);
  const [sentMsg, setSentMsg] = useState("");
  const reset = () => { setStep("identify"); setPassword(""); setBusy(false); };

  const lookup = async (e) => {
    e.preventDefault();
    if (!ident.trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/auth/lookup", { identifier: ident.trim() });
      setLookupRes(r.data);
      if (r.data.kind === "staff" || r.data.kind === "both") setStep(r.data.has_password ? "password" : "no_password");
      else if (r.data.kind === "resident") setStep("resident");
      else if (r.data.kind === "pending") setStep("pending");
      else setStep("unknown");
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't check that right now."); }
    finally { setBusy(false); }
  };

  const loginWithPassword = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/auth/password/login", { email: ident.trim(), password });
      setUser(res.data);
      navigate(res.data.must_change_password ? `/auth/create-password?next=${encodeURIComponent(postLoginDest())}` : postLoginDest(), { replace: true });
    } catch (err) { toast.error(err?.response?.data?.detail || "Sign-in failed."); }
    finally { setBusy(false); }
  };

  // Both "Forgot password" and "no password yet" end the same way: an emailed link to a page
  // where they type a new password. Typing is what a mail scanner can't do, so no link burns.
  const forgot = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/password/forgot", { email: ident.trim() });
      setSentMsg(r.data.message); setStep("sent");
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't send that right now."); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen w-full overflow-x-hidden grid lg:grid-cols-2 font-body">
      <div className="hidden lg:flex flex-col justify-between bg-ink p-12 relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-[0.06]" />
        <div className="relative z-10 flex items-center gap-2 text-white">
          <div className="h-9 w-9 rounded-xl bg-redline-500 flex items-center justify-center">
            <MessageSquareText className="h-5 w-5 text-white" strokeWidth={1.5} />
          </div>
          <span className="font-heading font-extrabold text-lg tracking-tight">Ask Redline®</span>
        </div>
        <div className="relative z-10">
          <h2 className="text-white font-heading text-3xl font-extrabold leading-tight tracking-tight">
            Your community's<br />concierge on the line,<br />24/7.
          </h2>
          <p className="text-sand-300 mt-4 max-w-sm">
            Residents call or text and get grounded answers from your own documents, personal by-name greetings, and human handoff when it matters.
          </p>
        </div>
        <div className="relative z-10 flex flex-col gap-3">
          <div className="flex items-center gap-2 text-sand-300 text-sm">
            <ShieldCheck className="h-4 w-4 text-redline-400" strokeWidth={1.5} />
            Hard tenant isolation · TCPA-aware · audit logged
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-8 bg-sand-50 min-w-0">
        <div className="w-full max-w-sm min-w-0">
          <div className="lg:hidden flex items-center gap-2 mb-10">
            <div className="h-9 w-9 rounded-xl bg-redline-500 flex items-center justify-center">
              <MessageSquareText className="h-5 w-5 text-white" strokeWidth={1.5} />
            </div>
            <span className="font-heading font-extrabold text-lg">Ask Redline®</span>
          </div>
          {brand ? (
            <div data-testid="login-community-brand" className="flex items-center gap-3">
              {logoUrl
                ? <img src={logoUrl} alt={brand.community} className="h-12 w-12 rounded-xl object-contain bg-white border border-sand-200 p-1" />
                : <div className="h-12 w-12 rounded-xl flex items-center justify-center text-white font-heading font-bold text-lg" style={{ backgroundColor: accent || "#e11d48" }}>{(brand.community || "?").charAt(0)}</div>}
              <div>
                <h1 className="font-heading text-2xl font-extrabold tracking-tight text-ink leading-tight">{brand.community}</h1>
                <p className="text-sm text-muted-foreground">Board &amp; staff sign in</p>
              </div>
            </div>
          ) : (
            <>
              <h1 className="font-heading text-3xl font-extrabold tracking-tight text-ink">Sign in</h1>
              <p className="text-muted-foreground mt-2">One door for everyone — managers, board members and residents. Enter the email or mobile your community has for you.</p>
            </>
          )}

          {/* One front door: type your email (or mobile) and we show only the door that fits you. */}
          <div ref={doorRef} className="mt-8 w-full" data-testid="login-front-door">
            {step === "identify" && (
              <form onSubmit={lookup} data-testid="login-identify-form">
                <label className="text-sm font-medium text-ink mb-2 block">Email or mobile number</label>
                <input data-testid="login-identifier" autoFocus value={ident} onChange={(e) => setIdent(e.target.value)} placeholder="you@managementco.com"
                  className="w-full rounded-full bg-white border border-sand-200 px-4 py-3 text-sm outline-none focus:border-ink transition-colors" />
                <button data-testid="login-continue" type="submit" disabled={busy} style={accent ? { backgroundColor: accent } : undefined}
                  className="mt-3 w-full rounded-full bg-ink text-white font-semibold py-3 text-sm hover:bg-ink-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                  {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> One moment…</> : "Continue"}
                </button>
              </form>
            )}

            {step === "password" && (
              <form onSubmit={loginWithPassword} data-testid="login-password-form">
                <div className="text-sm text-ink mb-3">Signing in as <span className="font-semibold">{ident}</span> <button type="button" onClick={reset} className="text-redline-600 font-semibold ml-1">change</button></div>
                <input data-testid="login-password" type="password" autoFocus value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password"
                  className="w-full rounded-full bg-white border border-sand-200 px-4 py-3 text-sm outline-none focus:border-ink transition-colors" />
                <button data-testid="login-submit" type="submit" disabled={busy} style={accent ? { backgroundColor: accent } : undefined}
                  className="mt-3 w-full rounded-full bg-ink text-white font-semibold py-3 text-sm hover:bg-ink-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                  {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Signing in…</> : "Sign in"}
                </button>
                <div className="flex items-center justify-between mt-3 text-sm">
                  <button type="button" data-testid="login-forgot" onClick={forgot} disabled={busy} className="font-semibold text-redline-600 hover:text-redline-700 disabled:opacity-60">Forgot password?</button>
                </div>
              </form>
            )}

            {step === "no_password" && (
              <div data-testid="login-no-password" className="rounded-2xl border border-sand-200 bg-white p-4">
                <div className="font-heading font-bold text-ink text-sm">Let's set your password</div>
                <p className="text-sm text-ink-700 mt-1 leading-relaxed">Your account (<span className="font-semibold">{ident}</span>) doesn't have a password yet. We'll email you a link to create one — after that you sign in with it from any device.</p>
                <button data-testid="login-set-password" onClick={forgot} disabled={busy} style={accent ? { backgroundColor: accent } : undefined}
                  className="mt-3 w-full rounded-full bg-ink text-white font-semibold py-3 text-sm hover:bg-ink-700 transition-colors disabled:opacity-60">
                  {busy ? "Sending…" : "Email me a set-password link"}
                </button>
                <button type="button" data-testid="login-change-email" onClick={reset} className="mt-2 w-full text-xs text-ink/50">Use a different email</button>
              </div>
            )}

            {step === "resident" && (
              <ResidentDoor identifier={ident.trim()} lookup={lookupRes} accent={accent} onBack={reset} testPrefix="login-res" />
            )}

            {step === "pending" && (
              <div data-testid="login-pending" className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
                <div className="font-heading font-bold text-ink text-sm">Your registration is waiting on the office</div>
                <p className="text-sm text-ink-700 mt-1 leading-relaxed">You signed up, and a manager still needs to confirm you live in the community. You'll be able to set a password as soon as they do — usually within a day.</p>
                <button type="button" onClick={reset} className="mt-3 text-sm font-semibold text-redline-600">Try another</button>
              </div>
            )}

            {step === "unknown" && (
              <div data-testid="login-unknown" className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
                <div className="font-heading font-bold text-ink text-sm">We don't have that on file</div>
                <p className="text-sm text-ink-700 mt-1 leading-relaxed">Try the email or mobile number your community has for you — often the one your invitation went to. Residents can also activate with the account number on their statement.</p>
                <div className="flex flex-wrap gap-3 mt-3 text-sm">
                  <button type="button" onClick={reset} className="font-semibold text-redline-600">Try another</button>
                  <button type="button" data-testid="login-use-account-number" onClick={() => setStep("resident_account")} className="font-semibold text-ink/70 hover:text-ink">I'm a resident — use my account number</button>
                </div>
              </div>
            )}

            {step === "resident_account" && (
              <ResidentDoor identifier={ident.trim()} lookup={{}} initialStep="account" accent={accent} onBack={reset} testPrefix="login-res" />
            )}

            {step === "sent" && (
              <div data-testid="login-sent" className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" strokeWidth={1.75} />
                  <div>
                    <div className="font-heading font-bold text-ink text-sm">Check your email</div>
                    <p className="text-sm text-ink-700 mt-1 leading-relaxed">{sentMsg}</p>
                    <p className="text-xs text-ink/60 mt-1.5 leading-relaxed">The link is good for 2 hours. Open it, type a new password, and you're in.</p>
                    <button data-testid="login-sent-back" onClick={reset} className="text-sm font-semibold text-redline-600 hover:text-redline-700 mt-2 transition-colors">Back</button>
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3 my-6">
              <div className="h-px bg-sand-200 flex-1" />
              <span className="text-xs text-muted-foreground font-medium">or</span>
              <div className="h-px bg-sand-200 flex-1" />
            </div>
            <div data-testid="google-login-btn" className="flex w-full overflow-hidden">
              <GoogleLogin onSuccess={handleGoogleSuccess} onError={() => toast.error("Google sign in was cancelled or failed.")}
                theme="outline" size="large" text="continue_with" shape="pill" width={String(googleWidth)} />
            </div>
            <p className="text-xs text-muted-foreground mt-2 leading-relaxed">Google works when the Google account matches the email on your Ask Redline account.</p>
          </div>

          <p className="text-xs text-muted-foreground mt-6 leading-relaxed">
            By continuing you agree to our acceptable-use and privacy terms. Conversations are visible only to your community's authorized admins.
          </p>
          <a href={brandSlug ? `/c/${brandSlug}` : "/"} className="inline-block mt-8 text-sm font-semibold text-redline-600 hover:text-redline-700 transition-colors">← Back to {brandSlug ? "community page" : "site"}</a>
        </div>
      </div>
    </div>
  );
}
